import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Flex, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { EditOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';

import { ActionButton } from '@hdi-seguros/web-components.action-button';

import { SavedQuotesContext } from '../../contexts/SavedQuotes';

import { SavedQuotesTableInterface } from '../../interface';

import { encrypt } from '../../../../../../shared/utils';
import { QuotationGroups } from '../../../../NewQuote/containers';

interface DataRowInterface extends SavedQuotesTableInterface {}

export const SavedQuotesTable = () => {
  const { 
    dataTable, 
    groupsList, 
    isLoadingTable,
    getQuotationGroupByArticle,
  } = useContext(SavedQuotesContext);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const navigate = useNavigate();

  const onResumeQuote = useCallback((quotationNumber: string, article: string, operation: string) => {
    const groupSelected = getQuotationGroupByArticle(article);
    const quotationInfo = { quotationNumber, groupSelected, tipoOperacion: operation };
    const parseQuoteInfo = encrypt<{ quotationNumber: string, groupSelected: QuotationGroups, tipoOperacion: string }>(quotationInfo);
    navigate(`/cotizador/nueva-cotizacion/${parseQuoteInfo}`);
  }, [navigate, getQuotationGroupByArticle]);

  const HEADERS_TABLE: ColumnsType<DataRowInterface> = useMemo(
    () => [
      {
        dataIndex: 'nameAndSurname',
        key: 'nameAndSurname',
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          a.nameAndSurname.localeCompare(b.nameAndSurname),
        title: 'Apellido y Nombre',
      },
      {
        dataIndex: 'branchDescription',
        key: 'branchDescription',
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          a.branchDescription.localeCompare(b.branchDescription),
        title: 'Rama',
      },
      {
        dataIndex: 'articleDescription',
        key: 'articleDescription',
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          a.articleDescription.localeCompare(b.articleDescription),
        title: 'Artículo',
      },
      {
        dataIndex: 'entryDate',
        key: 'entryDate',
        render: (value: string) => (
          <span>{value ? dayjs(value, 'DD-MM-YYYY').format('DD/MM/YYYY') : '-'}</span>
        ),
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          dayjs(a.entryDate, 'DD-MM-YYYY').unix() - dayjs(b.entryDate, 'DD-MM-YYYY').unix(),
        title: 'Fecha de Ingreso',
      },
      {
        dataIndex: 'operationDescription',
        key: 'operationDescription',
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          a.operationDescription.localeCompare(b.operationDescription),
        title: 'Operación',
      },
      {
        dataIndex: 'quotationNumber',
        key: 'quotationNumber',
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          Number(a.quotationNumber) - Number(b.quotationNumber),
        title: 'Nro. Cotización',
      },
      {
        dataIndex: 'state',
        isMultiple: true,
        key: 'state',
        sorter: (a: DataRowInterface, b: DataRowInterface) =>
          a.state.localeCompare(b.state),
        title: 'Estado',
      },
      {
        dataIndex: 'action',
        key: 'actions',
        render: (
          _: DataRowInterface,
          { quotationNumber, article, allowResume, operation }: DataRowInterface
        ) => (
          <Flex justify='center'>
            <ActionButton
              label=''
              icon={<EditOutlined />}
              onClick={() => onResumeQuote(quotationNumber, article, operation)}
              type='secondary'
              disabled={!allowResume}
            />
          </Flex>
        ),
        title: 'Acciones',
        width: '7%',
        align: 'center',
      },
    ],
    [navigate, groupsList]
  );

  const onPageSizeChange = useCallback(
    (current: number, newSize: number) => {
      if (current === pageSize) return;

      setPageSize(newSize);
    },
    [pageSize, setPageSize]
  );

  return (
    <Table
      bordered
      columns={HEADERS_TABLE}
      dataSource={dataTable}
      loading={isLoadingTable}
      scroll={{ x: 'auto' }}
      pagination={{
        current: currentPage,
        onChange: setCurrentPage,
        onShowSizeChange: onPageSizeChange,
        pageSize: pageSize,
        showSizeChanger: true,
        showTotal: (total: number) => {
          if (total > 1) return `${total} cotizaciones`;

          return 'Una cotización';
        },
      }}
    />
  );
};